import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    RootState,
    useAppSelector,
    useAppDispatch,
    getReviewChanges,
} from '../../redux';
import { useTranslation } from 'react-i18next';
import { Change, Log } from '../../models';
import { ChangeCard, ChangeModal, Icon, WidgetSkeleton } from '../../components';
import { useDashboardChangeModal } from '../../utils/useDashboardChangeModal';

const DISPLAY_LIMIT = 5;

function ChangesTeamReview() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { reviewChanges, reviewChangesStatus } = useAppSelector(
        (state: RootState) => state.changes,
    );
    const {
        modalChangeId,
        detailChange,
        openChangeModal,
        closeChangeModal,
    } = useDashboardChangeModal();
    const isLoading = (reviewChangesStatus === 'pending' || reviewChangesStatus === 'idle')
        && !reviewChanges?.length;

    useEffect(() => {
        dispatch(getReviewChanges({}));
    }, [dispatch]);

    const reloadReview = () => {
        dispatch(getReviewChanges({}));
    };

    const onRemoveCard = () => {
        closeChangeModal();
        reloadReview();
    };

    const onSelect = (change: Change) => {
        const logTeamReview = change.logs.filter((log) => log.type === Log.TYPE_CHANGE_TEAM_REVIEW_STARTED);
        if (logTeamReview[0]?.context?.url) {
            window.open(logTeamReview[0].context.url, '_blank');
        }
    };

    if (isLoading) {
        return <WidgetSkeleton rows={3} />;
    }

    if (!reviewChanges?.length) {
        return null;
    }

    const sorted = reviewChanges.sort((a: Change, b: Change) => b.priority - a.priority);
    const visible = sorted.slice(0, DISPLAY_LIMIT);
    const hiddenCount = sorted.length - visible.length;

    return (
        <div className="ChangesTeamReview rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-end justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <h3 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white">
                    <Icon type="eye" size={6} className="mr-1.5" />
                    {t('sprints.merge_requests.team_review')}
                    <span className="ml-1 text-base font-normal text-gray-500 dark:text-gray-400">({reviewChanges.length})</span>
                </h3>
                <Link className="text-xs text-indigo-600 hover:underline" to="sprints/mergerequests">
                    {t('common.see_all')}
                    <Icon type="right" className="inline" />
                </Link>
            </div>
            <div className="max-h-[450px] space-y-1.5 overflow-y-auto p-4">
                {visible.map((change: Change) => (
                    <ChangeCard
                        key={change._id}
                        change={change}
                        showProject
                        className="bg-blue-50/50 dark:bg-blue-950/20"
                        onClick={() => openChangeModal(change)}
                        onSelect={onSelect}
                    />
                ))}
                {hiddenCount > 0 && (
                    <Link to="/sprints/mergerequests" className="inline-block text-xs text-indigo-600 hover:underline">
                        {t('dashboard.show_more', { count: hiddenCount })}
                    </Link>
                )}
            </div>

            {detailChange && detailChange._id === modalChangeId && detailChange.project?.name && (
                <ChangeModal
                    project={detailChange.project}
                    change={detailChange}
                    show={true}
                    showProjectName={true}
                    onClose={closeChangeModal}
                    onRemove={onRemoveCard}
                />
            )}
        </div>
    );
}

export default ChangesTeamReview;
